import { type IMapLayerWfsOptions } from '../layers/MapLayer.ts';
import {
  buildWfsDescribeFeatureTypeUrl,
  parseWfsDescribeFeatureType,
} from '../layers/wfs/parseWfsDescribeFeatureType.ts';
import { type LayerMetadataResult } from './LayerMetadata.ts';

/**
 * Normalizza il tipo di geometria GML in un'etichetta leggibile,
 * coerente con quella del resolver ArcGIS. I tipi non mappati
 * mantengono il valore grezzo del servizio.
 */
const GEOMETRY_TYPE_LABELS: Record<string, string> = {
  Point: 'Punti',
  MultiPoint: 'Punti multipli',
  LineString: 'Linee',
  Curve: 'Linee',
  MultiLineString: 'Linee',
  MultiCurve: 'Linee',
  Polygon: 'Poligoni',
  Surface: 'Poligoni',
  MultiPolygon: 'Poligoni',
  MultiSurface: 'Poligoni',
};

/**
 * Introspezione degli attributi di un feature type WFS tramite
 * DescribeFeatureType. Non lancia mai: ogni errore è mappato in un
 * `LayerMetadataResult`.
 */
export async function fetchWfsLayerMetadata(
  rawUrl: string,
  wfs: IMapLayerWfsOptions | undefined,
): Promise<LayerMetadataResult> {
  // Senza typeName la DescribeFeatureType non è indirizzabile: il
  // chiamante non dovrebbe arrivare qui (introspezione non abilitata)
  if (!wfs?.typeName) return { status: 'invalid-response' };

  let response: Response;
  try {
    response = await fetch(buildWfsDescribeFeatureTypeUrl(rawUrl, wfs));
  } catch {
    return { status: 'unreachable' };
  }

  if (!response.ok) return { status: 'unreachable' };

  try {
    const description = parseWfsDescribeFeatureType(
      await response.text(),
      wfs.typeName,
    );

    return {
      status: 'ok',
      metadata: {
        attributes: description.fields,
        geometryType: description.geometryType
          ? (GEOMETRY_TYPE_LABELS[description.geometryType] ??
            description.geometryType)
          : undefined,
      },
    };
  } catch {
    return { status: 'invalid-response' };
  }
}
